<template>
    <nav class="layout-breadcrumb tw:flex tw:justify-between">
        <ol class="tw:w-4/5">
            <li v-if="isHome">
                <span>Dashboard</span>
            </li>
            <li v-else>
                <router-link :to="home.to" style="color: inherit">
                    <i :class="home.icon" />
                </router-link>
            </li>
            <li v-if="!isHome && breadcrumbItems.length > 0" class="layout-breadcrumb-chevron">/</li>
            <template v-if="!isHome">
                <template v-for="(crumb, i) in breadcrumbItems" :key="i">
                    <li class="tw:capitalize">
                        <router-link v-if="crumb.to && i !== breadcrumbItems.length - 1" :to="crumb.to">
                            {{ crumb.text }}
                        </router-link>
                        <span v-else>{{ crumb.text }}</span>
                        <a
                            v-if="crumb.to?.name === 'course-dashboard' && editAccess"
                            href="#"
                            style="display: inline-block"
                            class="p-r-none p-l-sm"
                            @click.prevent="navigateToEdit"
                        >
                            <i v-if="crumb.text !== 'Dashboard'" class="uil uil-pen" />
                        </a>
                    </li>
                    <li v-if="i !== breadcrumbItems.length - 1" class="layout-breadcrumb-chevron">/</li>
                </template>
            </template>
        </ol>
        <portal-target name="breadcrumb-actions" />
    </nav>
    <Divider />
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useLoading } from '@/composables/useLoading.js';
import Course from '@/models/Course.js';
import { gate } from '@/plugins/vue-gate/Index.js';

const route = useRoute();
const router = useRouter();
const store = useStore();
const home = { icon: 'pi pi-home', to: '/' };
const fallbackBreadcrumbs = ref([]);

const isHome = computed(() => route.path === '/');

const storeBreadcrumbs = computed(() => store.state.Breadcrumbs.breadcrumbs);

const breadcrumbItems = computed(() => {
    if (storeBreadcrumbs.value && storeBreadcrumbs.value.length > 0) {
        return storeBreadcrumbs.value;
    }
    return fallbackBreadcrumbs.value;
});

const course = computed(() => {
    return Course.query().with('course_settings').find(route.params.courseId);
});

const editAccess = computed(() => {
    if (!course.value) {
        return false;
    }

    const allowed = gate.allows('update', course.value);

    if (allowed && !store.getters['Auth/user'].is_admin) {
        const array = [
            new Date(course.value.course_settings.start_date) < new Date(new Date()),
            course.value.in_progress,
        ];

        return !array.includes(true);
    }

    return allowed;
});

const globalLoading = useLoading();

function navigateToEdit() {
    const loader = globalLoading.open();
    router.push({ name: 'course-edit-content', params: { courseId: course.value.id } }).finally(() => {
        loader.close();
    });
}

function setFallbackBreadcrumbs() {
    if (route.meta.breadcrumb) {
        fallbackBreadcrumbs.value = route.meta.breadcrumb.map(text => ({ text }));
        return;
    }

    fallbackBreadcrumbs.value = route.fullPath
        .split('?')[0]
        .split('/')
        .filter(item => item !== '')
        .filter(item => isNaN(Number(item)))
        .map(item => {
            const withSpaces = item.replace(/-/g, ' ');
            return { text: withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1) };
        });
}

watch(
    route,
    () => {
        setFallbackBreadcrumbs();
    },
    { immediate: true }
);
</script>
